import { motion } from 'motion/react'
import type { CustomerOrder } from '../../customerOrdersApi'
import type { ShopLanguage, ShopLocale, ShopMessageKey } from '../../../i18n'
import { customerMotionTransition } from '../customer-area'
import { toOrderStatusLabel } from '../../helpers/status'
import { formatDateTime } from '../../helpers/formatters'
import { isSafeExternalUrl, type CustomerOrderDetailLabels } from './orderDetailShared'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type CustomerOrderHeroSectionProps = {
  orderDetail: CustomerOrder
  language: ShopLanguage
  locale: ShopLocale
  labels: CustomerOrderDetailLabels
  reducedMotion: boolean
  t: TranslateFn
}

const resolveStatusBadgeClassName = (status: CustomerOrder['status']) => {
  if (status === 'shipped') {
    return 'border-(--color-status-info-border) bg-(--color-status-info-bg) text-(--color-status-info)'
  }
  if (status === 'paid') {
    return 'border-(--color-status-success-border) bg-(--color-status-success-bg) text-(--color-status-success)'
  }
  if (status === 'cancelled') {
    return 'border-(--color-status-error-border) bg-(--color-status-error-bg) text-(--color-status-error)'
  }
  return 'border-(--color-status-warning-border) bg-(--color-status-warning-bg) text-(--color-status-warning)'
}

export const CustomerOrderHeroSection = ({
  orderDetail,
  language,
  locale,
  labels,
  reducedMotion,
  t,
}: CustomerOrderHeroSectionProps) => (
  <section className="ca-card-shell ca-theme-transition rounded-xl px-5 py-5">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-wide text-(--color-text-muted)">
          {labels.orderPrefix}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <h2 className="truncate text-xl font-semibold tracking-tight text-(--color-text-primary)">
            #{orderDetail.orderNumber}
          </h2>
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${resolveStatusBadgeClassName(
              orderDetail.status,
            )}`}
          >
            {toOrderStatusLabel(orderDetail.status, language)}
          </span>
        </div>
        <p className="mt-1 text-xs text-(--color-text-secondary)">
          {labels.orderPlaced} {formatDateTime(orderDetail.createdAt, locale, t('common.never'))}
        </p>
      </div>

      {orderDetail.trackingCode ? (
        <span className="inline-flex items-center rounded-full border border-(--color-border-default) bg-(--color-bg-surface-muted) px-3 py-1 text-xs font-semibold text-(--color-text-secondary)">
          {labels.inTransit}: {orderDetail.trackingCode}
        </span>
      ) : null}
    </div>

    {isSafeExternalUrl(orderDetail.trackingUrl) ? (
      <div className="mt-3">
        <motion.a
          href={orderDetail.trackingUrl}
          target="_blank"
          rel="noreferrer noopener"
          whileTap={reducedMotion ? undefined : { scale: 0.985 }}
          transition={customerMotionTransition}
          className="ca-btn-secondary inline-flex h-9 items-center px-3 text-sm font-semibold"
        >
          {labels.trackOrder}
        </motion.a>
      </div>
    ) : null}
  </section>
)
